import { FC, lazy, Suspense, useContext } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

// pages
import { ForgotPassword } from '../pages/auth/forgotPassword'
import { ResetPassword } from '../pages/auth/resetPassword'
import { SetPassword } from '../pages/auth/setPassword'
import { Cases } from '../pages/main/cases'
import { Courts } from '../pages/main/courts'
import { Dashboard } from '../pages/main/dashboard'
import { Contacts } from '../pages/main/contacts'
import ConflictCheck from '../components/conflictCheck'
import { Profile } from '../pages/main/profile'
import { Users } from '../pages/main/users'
import { Entries } from '../pages/main/entries'
import { EntriesDashboard } from '../pages/main/entries/EntriesDashboard'
import InterestEntries from '../pages/main/billing/InterestEntries'
import { InterestEntriesContextProvider } from '../context/InterestEntriesContext'
import { LawType } from '../pages/main/lawType'
import { CustomTasks } from '../pages/main/customTasks.tsx'
import FeeTypes from '../pages/main/entries/FeeTypes'
import Categories from '../pages/main/entries/Categories'
import Departments from '../pages/main/departments'
import Billing from '../pages/main/billing'
import GenerateBills from '../pages/main/billing/GenerateBills'
import BillDetail from '../pages/main/billing/BillDetail'
import EditBill from '../pages/main/billing/EditBill'
import BillView from '../pages/main/billing/BillView'
import RecordPayment from '../pages/main/billing/RecordPayment'
import DepositFund from '../pages/main/accounts/DepositFund'
import { AccountingPeriodClose } from '../components/accounts/period-close'

// components
import CreateNewCase from '../components/cases/createNewCase'
import { Layout } from '../components/layout'
import { CourtDetail } from '../components/courts/CourtDetail'
import { CasesAgainstCaseTypeListing } from '../components/lawType/CasesAgainstCaseTypeListing'
import { LawTypeDetail } from '../components/lawType/LawTypeDetail'
import {
  AUTH_LINKS,
  BILL_VIEW_ROUTE_WITH_TOKEN,
  CASE_DETAIL_ROUTE_WITH_ID,
  COURT_DATES_ROUTE,
  DASHBOARD_ROUTE,
  DUPLICATE_CASE_ROUTE_WITH_ID,
  DUPLICATE_CONSULT_ROUTE_WITH_ID,
  FIRM_DASHBOARD_ROUTE,
  ROOT_ROUTE
} from '../constants'
import { AuthContext, CaseContextProvider } from '../context'
import { canAccessReports, getToken, hasAdminAccess } from '../utils'
import { PrivateRoute } from './PrivateRoute'
import { SessionWatcher } from '../components/auth/SessionWatcher'
import { Accounts } from '../pages/main/accounts'
import ClientLedger from '../components/accounts/clientLedger'
import ChartsOfAccountDetails from '../components/accounts/chartsofAccounts/detail'
import BankReconciliation from '../components/accounts/bank-reconciliation'
import BankReconciliationSubitemHistory from '../components/accounts/bank-reconciliation-subitem-history'
import ReferralSources from '../pages/main/referralSources'
import { EntryContextProvider } from '../context/EntryContext'
import ClientDetailPage from '../pages/main/contacts/ClientDetailPage'
import Referrals from '../pages/main/referralSources/Referrals'
import FundRequestDetail from '../pages/main/billing/FundRequestDetail'
import Reports from '../components/reports'
import Docs from '../pages/main/docs'
import GenerateFundRequests from '../pages/main/billing/GenerateFundRequests'
import Vendors from '../pages/main/vendors'
import Offices from '../pages/main/offices'
import PayableDetail from '../components/accounts/accountPayables/PayableDetail'
import BatchEmails from '../pages/main/accounting/batchEmails/BatchEmails'
import BatchEmailDetail from '../pages/main/accounting/batchEmails/BatchEmailDetail'
import ReconciliationPaymentsListing from '../components/accounts/bank-reconciliation-subitem-history/ReconciliationPaymentsListing'
import { FinalBillsContextProvider } from '../context/FinalBillsContext'
import ActivityPage from '../pages/main/activity'
import { ContactsDetailPage } from '../pages/main/contacts/ContactsDetail'
import QueueMonitor from '../pages/main/admin/QueueMonitor'
import FirmDashboard from '../pages/main/firmDashboard'
import CourtDates from '../pages/main/courtDates'

const Login = lazy(() => import('../pages/auth/login'))
const CaseDetail = lazy(() => import('../pages/main/cases/CaseDetail'))
const PageNotFound = lazy(() => import('../pages/404'))

const MainRoutes: FC = (): JSX.Element => {
  const {
    isAdmin,
    isAttorney,
    isInvestigator,
    currentUser,
    isLoading,
    isParalegal,
    isDcpp,
    isStaff,
    isLoggedIn,
    isAccountant,
    isSuperAdmin
  } = useContext(AuthContext)

  return (
    <BrowserRouter>
      <SessionWatcher />
      <Routes>
        {!getToken() && !isLoggedIn && (
          <Route path={ROOT_ROUTE} element={<Navigate replace to={AUTH_LINKS.LOGIN_LINK} />} />
        )}
        <Route path={AUTH_LINKS.SET_PASSWORD} element={<SetPassword />} />
        <Route path={AUTH_LINKS.RESET_PASSWORD} element={<ResetPassword />} />
        <Route path={AUTH_LINKS.FORGET_PASSWORD_LINK} element={<ForgotPassword />} />
        <Route path={BILL_VIEW_ROUTE_WITH_TOKEN} element={<BillView />} />

        <Route
          path={AUTH_LINKS.LOGIN_LINK}
          element={
            <Suspense fallback={null}>
              <Login />
            </Suspense>
          }
        />

        <Route path={ROOT_ROUTE} element={<Layout />}>
          {(isAttorney || isParalegal) && (
            <Route
              path={ROOT_ROUTE}
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
          )}

          {hasAdminAccess(currentUser) && (
            <>
              <Route path={ROOT_ROUTE} element={<Navigate replace to="/matters" />} />
              <Route
                path={'/activity-tracking'}
                element={
                  <PrivateRoute>
                    <ActivityPage />
                  </PrivateRoute>
                }
              />
              <Route
                path={FIRM_DASHBOARD_ROUTE}
                element={
                  <PrivateRoute>
                    <FirmDashboard />
                  </PrivateRoute>
                }
              />
            </>
          )}
          {(isAdmin || isSuperAdmin) && (
            <Route
              path={'/admin/background-jobs'}
              element={
                <PrivateRoute>
                  <QueueMonitor />
                </PrivateRoute>
              }
            />
          )}
          {(isInvestigator || isDcpp || isStaff) && (
            <Route path={ROOT_ROUTE} element={<Navigate replace to="/matters" />} />
          )}
          {/* Outside the hasAdminAccess block on purpose: the Client Ledger is the one Accounting
              page every internal role may reach. Which matters they may pick is enforced by the
              API, not by this route. */}
          <Route
            path="/accounts/client-ledger"
            element={
              <PrivateRoute>
                <CaseContextProvider>
                  <ClientLedger />
                </CaseContextProvider>
              </PrivateRoute>
            }
          />
          <Route
            path="/courts/:id"
            element={
              <PrivateRoute>
                <CourtDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/law-type/:id"
            element={
              <PrivateRoute>
                <LawTypeDetail />
              </PrivateRoute>
            }
          />
          <Route
            path="/law-type/:lawId/case-type/:id"
            element={
              <PrivateRoute>
                <CasesAgainstCaseTypeListing />
              </PrivateRoute>
            }
          />
          {((currentUser && (isAttorney || isParalegal)) || !getToken()) && (
            <Route
              path={DASHBOARD_ROUTE}
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
          )}
          {(!isInvestigator || !getToken()) && (
            <Route
              path="/courts"
              element={
                <PrivateRoute>
                  <Courts />
                </PrivateRoute>
              }
            />
          )}
          <Route
            path="/matters"
            element={
              <PrivateRoute>
                <CaseContextProvider>
                  <Cases />
                </CaseContextProvider>
              </PrivateRoute>
            }
          />
          <Route
            path="/consults"
            element={
              <PrivateRoute>
                <CaseContextProvider>
                  <Cases />
                </CaseContextProvider>
              </PrivateRoute>
            }
          />
          {(!isInvestigator || !getToken()) && (
            <>
              <Route
                path="/matters/create"
                element={
                  <PrivateRoute>
                    <CaseContextProvider>
                      <CreateNewCase />
                    </CaseContextProvider>
                  </PrivateRoute>
                }
              />

              <Route
                path="/consults/create"
                element={
                  <PrivateRoute>
                    <CaseContextProvider>
                      <CreateNewCase />
                    </CaseContextProvider>
                  </PrivateRoute>
                }
              />

              {/* Duplicate: the create form pre-filled from matter `:id`. */}
              {[DUPLICATE_CASE_ROUTE_WITH_ID, DUPLICATE_CONSULT_ROUTE_WITH_ID].map(path => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <PrivateRoute>
                      <CaseContextProvider>
                        <CreateNewCase />
                      </CaseContextProvider>
                    </PrivateRoute>
                  }
                />
              ))}
            </>
          )}
          <Route
            path={CASE_DETAIL_ROUTE_WITH_ID}
            element={
              <Suspense fallback={null}>
                <PrivateRoute>
                  <CaseContextProvider>
                    <CaseDetail />
                  </CaseContextProvider>
                </PrivateRoute>
              </Suspense>
            }
          />
          <Route
            path="/users"
            element={
              <PrivateRoute allowed={isAdmin || isSuperAdmin}>
                <Users />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />

          <Route
            path="/conflict-checks"
            element={
              <PrivateRoute>
                <CaseContextProvider>
                  <ConflictCheck />
                </CaseContextProvider>
              </PrivateRoute>
            }
          />

          <Route
            path="/contacts/:type"
            element={
              <PrivateRoute>
                <CaseContextProvider>
                  <Contacts />
                </CaseContextProvider>
              </PrivateRoute>
            }
          />

          <Route
            path="/contacts/:type/:id"
            element={
              <PrivateRoute>
                <CaseContextProvider>
                  <ContactsDetailPage />
                </CaseContextProvider>
              </PrivateRoute>
            }
          />
          <Route
            path="/contacts/client/:clientNo"
            element={
              <PrivateRoute>
                <CaseContextProvider>
                  <FinalBillsContextProvider>
                    <ClientDetailPage />
                  </FinalBillsContextProvider>
                </CaseContextProvider>
              </PrivateRoute>
            }
          />
          <Route
            path="/law-type"
            element={
              <PrivateRoute>
                <LawType />
              </PrivateRoute>
            }
          />
          <Route
            path="/custom-tasks"
            element={
              <PrivateRoute>
                <CustomTasks />
              </PrivateRoute>
            }
          />
          {hasAdminAccess(currentUser) && (
            <Route
              path="/entries/dashboard"
              element={
                <PrivateRoute>
                  <EntriesDashboard />
                </PrivateRoute>
              }
            />
          )}
          <Route
            path="/entries/:type"
            element={
              <PrivateRoute>
                <EntryContextProvider>
                  <CaseContextProvider>
                    <Entries />
                  </CaseContextProvider>
                </EntryContextProvider>
              </PrivateRoute>
            }
          />
          {hasAdminAccess(currentUser) && (
            <Route
              path="/entries/interest"
              element={
                <PrivateRoute>
                  <InterestEntriesContextProvider>
                    <InterestEntries />
                  </InterestEntriesContextProvider>
                </PrivateRoute>
              }
            />
          )}
          {hasAdminAccess(currentUser) && (
            <Route
              path="/entries/expense/categories"
              element={
                <PrivateRoute>
                  <CaseContextProvider>
                    <Categories />
                  </CaseContextProvider>
                </PrivateRoute>
              }
            />
          )}
          {hasAdminAccess(currentUser) && (
            <Route
              path="/entries/legal/fee-types"
              element={
                <PrivateRoute>
                  <CaseContextProvider>
                    <FeeTypes />
                  </CaseContextProvider>
                </PrivateRoute>
              }
            />
          )}
          {hasAdminAccess(currentUser) && (
            <Route
              path="/departments"
              element={
                <PrivateRoute>
                  <CaseContextProvider>
                    <Departments />
                  </CaseContextProvider>
                </PrivateRoute>
              }
            />
          )}
          {(isAdmin || isAccountant || isParalegal || isAttorney) && (
            <Route
              path="/billing/:type"
              element={
                <PrivateRoute>
                  <CaseContextProvider>
                    <Billing />
                  </CaseContextProvider>
                </PrivateRoute>
              }
            />
          )}
          {(isAdmin || isAccountant || isAttorney || isParalegal) && (
            <Route
              path="/billing/generate-bills"
              element={
                <PrivateRoute>
                  <CaseContextProvider>
                    <GenerateBills />
                  </CaseContextProvider>
                </PrivateRoute>
              }
            />
          )}
          {hasAdminAccess(currentUser) && (
            <>
              <Route
                path="/accounts/:page"
                element={
                  <PrivateRoute>
                    <CaseContextProvider>
                      <Accounts />
                    </CaseContextProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/accounts/chartered/:id"
                element={
                  <PrivateRoute>
                    <CaseContextProvider>
                      <ChartsOfAccountDetails />
                    </CaseContextProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/accounts/bank-reconciliation"
                element={
                  <PrivateRoute>
                    <CaseContextProvider>
                      <BankReconciliation />
                    </CaseContextProvider>
                  </PrivateRoute>
                }
              />
              {/* <Route
                path="/accounts/payable-refunds"
                element={
                  <PrivateRoute>
                    <PayableRefundsContextProvider>
                      <PayableRefundsPage />
                    </PayableRefundsContextProvider>
                  </PrivateRoute>
                }
              /> */}
              <Route
                path="/accounts/payable/:voucherNo"
                element={
                  <PrivateRoute>
                    <CaseContextProvider>
                      <PayableDetail />
                    </CaseContextProvider>
                  </PrivateRoute>
                }
              />

              <Route
                path="/accounts/bank-reconciliation/history"
                element={
                  <PrivateRoute>
                    <CaseContextProvider>
                      <BankReconciliationSubitemHistory />
                    </CaseContextProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/accounts/bank-reconciliation/history/:id"
                element={
                  <PrivateRoute>
                    <CaseContextProvider>
                      <ReconciliationPaymentsListing />
                    </CaseContextProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/accounts/deposit-fund"
                element={
                  <PrivateRoute>
                    <DepositFund />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accounts/period-close"
                element={
                  <PrivateRoute>
                    <CaseContextProvider>
                      <AccountingPeriodClose />
                    </CaseContextProvider>
                  </PrivateRoute>
                }
              />
              <Route
                path="/accounts/batch-emails"
                element={
                  <PrivateRoute>
                    <BatchEmails />
                  </PrivateRoute>
                }
              />
              <Route
                path="/accounts/batch-emails/:id"
                element={
                  <PrivateRoute>
                    <BatchEmailDetail />
                  </PrivateRoute>
                }
              />
            </>
          )}

          <Route
            path={COURT_DATES_ROUTE}
            element={
              <PrivateRoute allowed={isAdmin || isSuperAdmin || isAttorney || isParalegal}>
                <CourtDates />
              </PrivateRoute>
            }
          />

          {canAccessReports(currentUser) && (
            <>
              <Route
                path="/reports"
                element={
                  <PrivateRoute>
                    <CaseContextProvider>
                      <Reports />
                    </CaseContextProvider>
                  </PrivateRoute>
                }
              />
            </>
          )}
          {/* Docs & Training — available to all authenticated roles */}
          <Route
            path="/docs"
            element={
              <PrivateRoute>
                <Docs />
              </PrivateRoute>
            }
          />
          <Route
            path="/docs/:slug"
            element={
              <PrivateRoute>
                <Docs />
              </PrivateRoute>
            }
          />
          {!isInvestigator && (
            <Route
              path="/vendors"
              element={
                <PrivateRoute>
                  <Vendors />
                </PrivateRoute>
              }
            />
          )}
          {(isAdmin || isAccountant || isParalegal || isAttorney) && (
            <Route
              path="/billing/bill-detail/:id"
              element={
                <PrivateRoute>
                  <BillDetail />
                </PrivateRoute>
              }
            />
          )}
          {(isAdmin || isAccountant || isParalegal || isAttorney) && (
            <Route
              path="/billing/fund-request-detail/:id"
              element={
                <PrivateRoute>
                  <FundRequestDetail />
                </PrivateRoute>
              }
            />
          )}
          {(isAdmin || isAccountant) && (
            <Route
              path="/billing/generate-fund-requests"
              element={
                <PrivateRoute>
                  <GenerateFundRequests />
                </PrivateRoute>
              }
            />
          )}
          {(isAdmin || isAccountant || isAttorney || isParalegal) && (
            <Route
              path="/billing/edit-bill/:id"
              element={
                <PrivateRoute>
                  <EditBill />
                </PrivateRoute>
              }
            />
          )}
          {(isAdmin || isAccountant || isParalegal || isAttorney) && (
            <Route
              path="/billing/record-payment"
              element={
                <PrivateRoute>
                  <CaseContextProvider>
                    <RecordPayment />
                  </CaseContextProvider>
                </PrivateRoute>
              }
            />
          )}
          {hasAdminAccess(currentUser) && (
            <>
              <Route path="/referrals/sources" element={<Referrals />} />
              <Route path="/referrals/types" element={<ReferralSources />} />
              <Route
                path="/profit-centers"
                element={
                  <PrivateRoute>
                    <Offices />
                  </PrivateRoute>
                }
              />
            </>
          )}
        </Route>

        {!isLoading && (
          <Route
            path="*"
            element={
              <Suspense fallback={null}>
                <PageNotFound />
              </Suspense>
            }
          />
        )}
      </Routes>
    </BrowserRouter>
  )
}

export default MainRoutes
